// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Disposal Sites CRUD
// TDSR, DMS, final disposal, recycler management
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Autocomplete,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect, Progress,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { DEBRIS_TYPE_LABELS } from '../../types/debris';
import type { DebrisDisposalSite, SiteStatus } from '../../types/debris';
import { WORKER_URL, TABLES, FIELDS } from '../../lib/api-switch';
import { queryRecords } from '../../api/client';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'full', label: 'Full' },
  { value: 'closed', label: 'Closed' },
];

const SITE_TYPES = [
  { value: 'tdsr', label: 'TDSR (Temporary Staging)' },
  { value: 'dms', label: 'DMS (Debris Management)' },
  { value: 'final_disposal', label: 'Final Disposal' },
  { value: 'recycler', label: 'Recycler' },
];

const STATUS_COLORS: Record<string, string> = {
  open: 'var(--color-success)', full: '#F86A0B', closed: '#939598',
};

const DEBRIS_TYPE_OPTIONS = Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

const EMPTY: Partial<DebrisDisposalSite> = {
  siteName: '', siteType: '', address: '', latitude: '', longitude: '',
  capacityCubicYards: '', currentVolume: '0', acceptedTypes: '[]',
  operatingHours: '', monitorName: '', monitorEmail: '',
  status: 'open', notes: '',
};

export default function DisposalSites() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    disposalSites, loadingSites,
    loadDisposalSites, createDisposalSite, updateDisposalSite, deleteDisposalSite,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisDisposalSite | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisDisposalSite | null>(null);
  const [form, setForm] = useState<Partial<DebrisDisposalSite>>({ ...EMPTY });
  const [formTypes, setFormTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Directory facilities for pre-fill
  const [dirFacilities, setDirFacilities] = useState<Array<{ value: string; label: string; street: string; city: string; state: string; zip: string; lat: string; lng: string }>>([]);
  const [dirFacilityPick, setDirFacilityPick] = useState<string | null>(null);

  useEffect(() => {
    if (!TABLES.facilities) return;
    const FF = FIELDS.facilities;
    queryRecords(TABLES.facilities, { limit: 200 }).then(res => {
      const DISPOSAL_TYPES = new Set(['disposal-site', 'debris-site']);
      setDirFacilities(res.records.filter((r: any) => DISPOSAL_TYPES.has(String(r[FF.type] ?? ''))).map((r: any) => ({
        value: String(r.record_id),
        label: String(r[FF.name] ?? `Facility #${r.record_id}`),
        street: String(r[FF.street] ?? ''),
        city: String(r[FF.city] ?? ''),
        state: String(r[FF.state] ?? ''),
        zip: String(r[FF.zip] ?? ''),
        lat: String(r[FF.latitude] ?? ''),
        lng: String(r[FF.longitude] ?? ''),
      })));
    }).catch(() => {});
  }, []);

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<{ value: string; lat: string; lng: string }[]>([]);
  const addrTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAddrSearch = (q: string) => {
    setField('address', q);
    if (addrTimeout.current) clearTimeout(addrTimeout.current);
    if (q.length < 3) { setAddrSuggestions([]); return; }
    addrTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER_URL}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setAddrSuggestions((data.items || []).filter((i: any) => i.position).map((i: any) => ({
          value: i.title || i.address?.label || q,
          lat: String(i.position.lat), lng: String(i.position.lng),
        })));
      } catch { /* ignore */ }
    }, 300);
  };

  useEffect(() => { loadDisposalSites(); }, [loadDisposalSites]);

  const iSites = incidentId ? disposalSites : [];

  // Build monitor dropdown from existing sites
  const knownMonitors = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    disposalSites.forEach(s => {
      if (s.monitorName?.trim()) map.set(s.monitorName, { name: s.monitorName, email: s.monitorEmail || '' });
    });
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [disposalSites]);
  const monitorSelectData = useMemo(() => knownMonitors.map(m => ({ value: m.name, label: m.name })), [knownMonitors]);

  const filtered = useMemo(() => {
    let list = iSites;
    if (statusFilter && statusFilter !== 'all') list = list.filter(s => s.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        (s.siteName || '').toLowerCase().includes(q) || (s.address || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.siteName || '').localeCompare(b.siteName || ''));
  }, [iSites, statusFilter, search]);

  const sorted = [...filtered].sort((a, b) => {
    if (!sortField) return 0;
    const va = (a as any)[sortField] ?? '';
    const vb = (b as any)[sortField] ?? '';
    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? n : -n;
  });

  const setField = (key: keyof DebrisDisposalSite, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setFormTypes([]); setDirFacilityPick(null); setModalOpen(true); };
  const openEdit = (s: DebrisDisposalSite) => {
    setEditTarget(s);
    setForm({ ...s });
    try { setFormTypes(JSON.parse(s.acceptedTypes || '[]')); } catch { setFormTypes([]); }
    setDirFacilityPick(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.siteName?.trim()) {
      notifications.show({ message: 'Site name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId, acceptedTypes: JSON.stringify(formTypes) };
      if (editTarget) {
        await updateDisposalSite(editTarget.id, payload);
        notifications.show({ message: 'Site updated', color: 'var(--color-success)' });
      } else {
        await createDisposalSite(payload);
        notifications.show({ message: 'Site created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteDisposalSite(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.siteName} deleted`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Disposal Sites</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingSites} onClick={() => loadDisposalSites()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Site</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search site name, address..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} site{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                {[
                  { label: 'Site Name', field: 'siteName', align: 'left' as const, extra: undefined },
                  { label: 'Type', field: 'siteType', align: 'left' as const, extra: undefined },
                  { label: 'Address', field: 'address', align: 'left' as const, extra: undefined },
                  { label: 'Status', field: 'status', align: 'left' as const, extra: undefined },
                  { label: 'Capacity', field: 'capacityCubicYards', align: 'right' as const, extra: undefined },
                  { label: 'Fill Level', field: '', align: 'left' as const, extra: { minWidth: 120 } },
                  { label: 'Monitor', field: 'monitorName', align: 'left' as const, extra: undefined },
                  { label: 'Actions', field: '', align: 'right' as const, extra: undefined },
                ].map(col => (
                  <th
                    key={col.label}
                    onClick={() => col.field && toggleSort(col.field)}
                    style={{
                      padding: '8px 16px',
                      textAlign: col.align,
                      fontWeight: 600,
                      color: sortField === col.field && col.field ? '#4B3FFF' : undefined,
                      whiteSpace: 'nowrap',
                      cursor: col.field ? 'pointer' : 'default',
                      userSelect: 'none',
                      ...col.extra,
                    }}>
                    {col.label}
                    {col.field && (
                      <span style={{ marginLeft: 4, opacity: sortField === col.field ? 1 : 0.25 }}>
                        {sortField === col.field && sortDir === 'desc' ? '↓' : '↑'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingSites ? 'Loading...' : 'No disposal sites found'}
                </td></tr>
              )}
              {sorted.map(s => {
                const cap = parseFloat(s.capacityCubicYards || '0');
                const vol = parseFloat(s.currentVolume || '0');
                const pct = cap > 0 ? Math.min((vol / cap) * 100, 100) : 0;
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                    onClick={() => openEdit(s)}>
                    <td style={{ padding: '8px 16px', fontWeight: 500 }}>{s.siteName || '-'}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <Badge size="sm" variant="light">
                        {SITE_TYPES.find(st => st.value === s.siteType)?.label ?? s.siteType ?? '-'}
                      </Badge>
                    </td>
                    <td style={{ padding: '8px 16px' }}>{s.address || '-'}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <Badge size="sm" color={STATUS_COLORS[s.status] ?? '#939598'}>{s.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                      {cap > 0 ? `${cap.toLocaleString()} CY` : '-'}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      {cap > 0 ? (
                        <Group gap={4}>
                          <Progress value={pct} size="sm" style={{ flex: 1 }}
                            color={pct >= 90 ? 'var(--color-error)' : pct >= 70 ? '#F86A0B' : 'var(--color-success)'} />
                          <Text size="xs" w={40} ta="right">{pct.toFixed(0)}%</Text>
                        </Group>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <Text size="sm">{s.monitorName || '-'}</Text>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <Group gap={4} justify="flex-end">
                        {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(s)}>
                          <IconPencil size={14} />
                        </ActionIcon>}
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(s)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Disposal Site' : 'Add Disposal Site'} centered size="lg">
        <Stack>
          {dirFacilities.length > 0 && (
            <Select
              label="Pre-fill from Directory facility"
              description="Selecting a facility auto-fills name, address, and coordinates"
              placeholder="Search directory facilities…"
              data={dirFacilities}
              value={dirFacilityPick}
              searchable
              clearable
              onChange={v => {
                setDirFacilityPick(v);
                if (!v) return;
                const fac = dirFacilities.find(f => f.value === v);
                if (!fac) return;
                const addr = [fac.street, fac.city, fac.state, fac.zip].filter(Boolean).join(', ');
                setForm(prev => ({ ...prev, siteName: fac.label, address: addr, latitude: fac.lat, longitude: fac.lng }));
              }}
            />
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Site Name" required value={form.siteName ?? ''}
              onChange={e => setField('siteName', e.target.value)} />
            <Select label="Site Type" data={SITE_TYPES} value={form.siteType || null}
              onChange={v => setField('siteType', v ?? '')} clearable />
            <Autocomplete label="Address" value={form.address ?? ''}
              onChange={handleAddrSearch}
              onOptionSubmit={val => {
                setField('address', val);
                const match = addrSuggestions.find(s => s.value === val);
                if (match) { setField('latitude', match.lat); setField('longitude', match.lng); }
              }}
              data={addrSuggestions.map(s => s.value)}
              placeholder="Start typing an address..." />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as SiteStatus) ?? 'open'}
              onChange={v => setField('status', v ?? 'open')} />
            <TextInput label="Latitude" value={form.latitude ?? ''}
              onChange={e => setField('latitude', e.target.value)} />
            <TextInput label="Longitude" value={form.longitude ?? ''}
              onChange={e => setField('longitude', e.target.value)} />
            <TextInput label="Capacity (CY)" value={form.capacityCubicYards ?? ''}
              onChange={e => setField('capacityCubicYards', e.target.value)} />
            <TextInput label="Current Volume (CY)" value={form.currentVolume ?? ''}
              onChange={e => setField('currentVolume', e.target.value)} />
            <TextInput label="Operating Hours" value={form.operatingHours ?? ''}
              onChange={e => setField('operatingHours', e.target.value)} placeholder="e.g. 7AM-5PM Mon-Sat" />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Tower Monitor</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Monitor Name" data={monitorSelectData} value={form.monitorName || null}
              onChange={v => {
                setField('monitorName', v ?? '');
                const m = knownMonitors.find(m => m.name === v);
                if (m) setField('monitorEmail', m.email);
              }}
              searchable clearable allowDeselect
              nothingFoundMessage="Type a new monitor name above"
            />
            <TextInput label="Monitor Email" value={form.monitorEmail ?? ''}
              onChange={e => setField('monitorEmail', e.target.value)} />
          </SimpleGrid>

          <MultiSelect label="Accepted Debris Types" data={DEBRIS_TYPE_OPTIONS}
            value={formTypes} onChange={setFormTypes} searchable />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Site'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Site" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.siteName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
